import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { switchMap, map, catchError, withLatestFrom } from 'rxjs/operators';

import { ELinksService } from '../../../core/services/e-links.service';
import * as ELinksActions from './e-links.actions';
import { selectELinksMeta, selectELinksLastQuery } from './e-links.selectors';
import {
  DEFAULT_E_LINKS_CURRENT_PAGE,
  DEFAULT_E_LINKS_PER_PAGE,
} from '../models/e-link.model';

@Injectable()
export class ELinksEffects {
  private actions$ = inject(Actions);
  private eLinksService = inject(ELinksService);
  private store = inject(Store);

  loadELinks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ELinksActions.loadELinks),
      switchMap((action) =>
        this.eLinksService
          .getELinksList({
            page: action.page ?? DEFAULT_E_LINKS_CURRENT_PAGE,
            perPage: action.perPage ?? DEFAULT_E_LINKS_PER_PAGE,
            search: action.search ?? '',
            sort: action.sort,
            sortDirection: action.sortDirection,
          })
          .pipe(
            map((response) =>
              ELinksActions.loadELinksSuccess({
                eLinks: response.data,
                meta: response.meta,
              })
            ),
            catchError((error) =>
              of(
                ELinksActions.loadELinksFailure({
                  error: error?.message ?? 'Failed to load E-Links',
                })
              )
            )
          )
      )
    )
  );

  deleteELink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ELinksActions.deleteELink),
      switchMap((action) =>
        this.eLinksService.deleteELink(action.uuid).pipe(
          map(() => ELinksActions.deleteELinkSuccess()),
          catchError((error) =>
            of(
              ELinksActions.mutationFailure({
                error: error?.message ?? 'Failed to delete E-Link',
              })
            )
          )
        )
      )
    )
  );

  reloadAfterMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ELinksActions.deleteELinkSuccess),
      withLatestFrom(
        this.store.select(selectELinksMeta),
        this.store.select(selectELinksLastQuery)
      ),
      map(([, meta, lastQuery]) =>
        ELinksActions.loadELinks({
          page: meta?.currentPage ?? DEFAULT_E_LINKS_CURRENT_PAGE,
          perPage: meta?.perPage ?? DEFAULT_E_LINKS_PER_PAGE,
          search: lastQuery.search,
          sort: lastQuery.sort,
          sortDirection: lastQuery.sortDirection,
        })
      )
    )
  );

  /* ------------------------------------------------------------------------ */
  /* Editor effects                                                            */
  /* ------------------------------------------------------------------------ */

  loadELinkOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ELinksActions.loadELinkOptions),
      switchMap(() =>
        this.eLinksService.getELinkOptions().pipe(
          map((options) => ELinksActions.loadELinkOptionsSuccess({ options })),
          catchError((error) =>
            of(
              ELinksActions.loadELinkOptionsFailure({
                error: error?.message ?? 'Failed to load E-Link options',
              })
            )
          )
        )
      )
    )
  );

  loadELink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ELinksActions.loadELink),
      switchMap(({ uuid }) =>
        this.eLinksService.getELinkById(uuid).pipe(
          map((eLink) => ELinksActions.loadELinkSuccess({ eLink })),
          catchError((error) =>
            of(
              ELinksActions.loadELinkFailure({
                error: error?.message ?? 'Failed to load E-Link',
              })
            )
          )
        )
      )
    )
  );

  saveELink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ELinksActions.saveELink),
      switchMap(({ uuid, payload }) => {
        const request$ = uuid
          ? this.eLinksService.updateELink(uuid, payload)
          : this.eLinksService.createELink(payload);

        return request$.pipe(
          map((eLink) => ELinksActions.saveELinkSuccess({ eLink })),
          catchError((error) =>
            of(
              ELinksActions.saveELinkFailure({
                error: error?.message ?? 'Failed to save E-Link',
                fieldErrors: extractFieldErrors(error),
              })
            )
          )
        );
      })
    )
  );
}

/**
 * Extracts field-level validation errors from a 422 response. The HttpService
 * rejects with `{ status, data, message }` where `data` is the raw response
 * body, e.g. `{ message, errors: { name: ['...'], 'settings[3]': ['...'] } }`.
 * Returns a flat `{ field: joinedMessage }` map, or undefined when absent.
 */
function extractFieldErrors(error: unknown): Record<string, string> | undefined {
  const body = (error as { data?: { errors?: Record<string, unknown> } })?.data;
  const rawErrors = body?.errors;

  if (!rawErrors || typeof rawErrors !== 'object') {
    return undefined;
  }

  const result: Record<string, string> = {};

  Object.entries(rawErrors).forEach(([field, messages]) => {
    result[field] = Array.isArray(messages) ? messages.join(' ') : String(messages ?? '');
  });

  return Object.keys(result).length ? result : undefined;
}
